// src/components/modules/devtools/hooks/devIdeSandboxHandlers.js

// Keep rebuild diagnostics readable without dropping the build output that
// commonly contains the only explanation for a failed command.
const rebuildDiagnosticText = (value) => {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  return JSON.stringify(value, null, 2) || String(value);
};

const buildOutputExcerpt = (value, maxLength = 20000) => {
  const text = rebuildDiagnosticText(value);
  return text.length > maxLength
    ? `[Earlier output omitted (${text.length - maxLength} characters).]\n${text.slice(-maxLength)}`
    : text;
};

const createSandboxHandlers = (deps) => {
  const {
    setBackendActionLoading,
    setFrontendActionLoading,
    setActionMessage,
    selectedFileMeta,
    setSaveErrorMessage,
    setSaveErrorOpen,
    // NEW: project scoping
    selectedProjectId,
  } = deps;

  const handleRestartSandboxBackend = async () => {
    setBackendActionLoading(true);
    setActionMessage("");

    try {
      const res = await fetch("/api/dev-ide/restart-sandbox-backend", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_id: selectedProjectId || null,
        }),
      });

      const data = await res.json().catch(() => null);

      if (res.ok && data && data.success) {
        const exitCode =
          typeof data.exit_code === "number" ? data.exit_code : 0;
        setActionMessage(
          `Sandbox Flask backend restart triggered (exit code ${exitCode}).`
        );
      } else {
        const errorText =
          (data && (data.error || data.error_output)) ||
          "Unknown error restarting backend.";
        setActionMessage(
          `Backend restart may have partially succeeded. Details: ${errorText}`
        );
      }
    } catch (err) {
      setActionMessage(
        `Error calling backend restart: ${err.message || String(err)}`
      );
    } finally {
      setBackendActionLoading(false);
    }
  };

  const handleRebuildSandboxFrontend = async () => {
    setFrontendActionLoading(true);
    setActionMessage("");
    try {
      const res = await fetch("/api/dev-ide/rebuild-sandbox-frontend", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_id: selectedProjectId || null,
        }),
      });

      // Read the raw response once, so a proxy/HTML/empty response can also
      // be diagnosed instead of silently becoming an "Unknown error".
      const responseText = await res.text();
      let parsed = null;
      try {
        parsed = JSON.parse(responseText);
      } catch (_) {
        // The HTTP status and a short response excerpt are reported below.
      }
      const data = parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? parsed
        : null;
      const exitCode = data && typeof data.exit_code === "number"
        ? data.exit_code
        : null;
      const backendSuccess = data && data.success === true;
      const succeeded = res.ok && backendSuccess && exitCode === 0;
      const status = succeeded
        ? "SUCCEEDED"
        : res.ok && backendSuccess && exitCode === null
          ? "NOT VERIFIED"
          : "FAILED";

      const diagnostics = [
        `Frontend rebuild: ${status}`,
        `HTTP status: ${res.status}${res.statusText ? ` ${res.statusText}` : ""}`,
        `Backend success: ${data && typeof data.success === "boolean" ? data.success : "not provided"}`,
        `Exit code: ${exitCode === null ? "not provided" : exitCode}`,
      ];

      if (data) {
        // Backend _run_tool returns stdout as output and stderr as error;
        // older handlers may also provide error_output.
        [
          ["Error (stderr)", data.error],
          ["Error output", data.error_output],
          ["Build output (stdout)", data.output],
        ].forEach(([label, value]) => {
          const content = buildOutputExcerpt(value);
          if (content) diagnostics.push(`${label}:\n${content}`);
        });
      } else {
        diagnostics.push(
          responseText
            ? `Unexpected non-JSON rebuild response (excerpt):\n${buildOutputExcerpt(responseText, 1000)}`
            : "Empty response from rebuild endpoint."
        );
      }

      if (!succeeded && data && !data.error && !data.error_output && !data.output) {
        diagnostics.push("No build output returned. Inspect the DEV IDE backend rebuild logs.");
      }
      setActionMessage(diagnostics.join("\n\n"));

      if (!succeeded && setSaveErrorMessage && setSaveErrorOpen) {
        const explanation = rebuildDiagnosticText(data?.error) ||
          rebuildDiagnosticText(data?.error_output) ||
          rebuildDiagnosticText(data?.output) ||
          (data ? "No failure details returned." : "Invalid or empty JSON response.");
        const brief = explanation.length > 500
          ? `…${explanation.slice(-500)}`
          : explanation;
        setSaveErrorMessage(
          `Frontend rebuild ${status === "NOT VERIFIED" ? "could not be verified" : "failed"} ` +
          `(HTTP ${res.status}, exit code ${exitCode === null ? "unknown" : exitCode}). ` +
          `${brief} See the action result for full diagnostics.`
        );
        setSaveErrorOpen(true);
      }
    } catch (err) {
      const detail = err?.message || String(err);
      setActionMessage(`Error calling frontend rebuild endpoint: ${detail}`);
      if (setSaveErrorMessage && setSaveErrorOpen) {
        setSaveErrorMessage(`Frontend rebuild request failed: ${detail}`);
        setSaveErrorOpen(true);
      }
    } finally {
      setFrontendActionLoading(false);
    }
  };

  const handleRunBackendScan = async () => {
    const params = new URLSearchParams();
    if (selectedProjectId) {
      params.set("project_id", selectedProjectId);
    }

    await fetch(
      `/api/dev-ide/run-backend-scan${
        params.toString() ? `?${params.toString()}` : ""
      }`,
      { method: "POST" }
    );
  };

  const handleRunFrontendScan = async () => {
    const params = new URLSearchParams();
    if (selectedProjectId) {
      params.set("project_id", selectedProjectId);
    }

    await fetch(
      `/api/dev-ide/run-frontend-scan${
        params.toString() ? `?${params.toString()}` : ""
      }`,
      { method: "POST" }
    );
  };

  const handleDescribeFile = async () => {
    if (!selectedFileMeta?.path) return;

    await fetch("/api/dev-ide/describe-file", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        path: selectedFileMeta.path,
        project_id: selectedProjectId || null,
      }),
    });
  };

  return {
    handleRestartSandboxBackend,
    handleRebuildSandboxFrontend,
    handleRunBackendScan,
    handleRunFrontendScan,
    handleDescribeFile,
  };
};

export default createSandboxHandlers;